import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { fmtRel } from '@/lib/format';
import { RiskBadge } from '@/components/common/RiskBadge';

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = (params.get('q') || '').toLowerCase();
  const [results, setResults] = useState(null);

  useEffect(() => {
    (async () => {
      const [assets, alerts, incidents] = await Promise.all([
        api.get('/assets').then(r => r.data),
        api.get('/alerts', { params: { limit: 200 } }).then(r => r.data),
        api.get('/incidents', { params: { limit: 200 } }).then(r => r.data),
      ]);
      const match = (s) => (s || '').toLowerCase().includes(q);
      setResults({
        assets: assets.filter(a => match(a.name) || match(a.location_name) || match(a.description)),
        alerts: alerts.filter(a => match(a.title) || match(a.category)),
        incidents: incidents.filter(i => match(i.title) || match(i.number) || match(i.description)),
      });
    })();
  }, [q]);

  if (!results) return <div className="p-6"><PageLoader /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader title={`Search results for "${q}"`} subtitle="Global search across assets, alerts and incidents" />
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-sm font-semibold mb-2">Assets ({results.assets.length})</div>
          {results.assets.map(a => (
            <Link key={a.id} to={`/app/assets/${a.id}`} className="block text-sm py-1.5 hover:text-primary border-b border-border last:border-none">
              {a.name} <span className="text-xs text-muted-foreground">— {a.location_name}</span>
            </Link>
          ))}
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold mb-2">Alerts ({results.alerts.length})</div>
          {results.alerts.map(a => (
            <Link key={a.id} to={`/app/alerts/${a.id}`} className="flex items-center gap-2 py-1.5 border-b border-border last:border-none hover:text-primary">
              <span className="flex-1 text-sm truncate">{a.title}</span>
              <RiskBadge severity={a.severity} />
            </Link>
          ))}
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold mb-2">Incidents ({results.incidents.length})</div>
          {results.incidents.map(i => (
            <Link key={i.id} to={`/app/incidents/${i.id}`} className="flex items-center gap-2 py-1.5 border-b border-border last:border-none hover:text-primary">
              <span className="font-mono text-xs">{i.number}</span>
              <span className="flex-1 text-sm truncate">{i.title}</span>
              <RiskBadge severity={i.severity} />
            </Link>
          ))}
        </Card>
      </div>
    </div>
  );
}
